import type { ResourceProps } from 'react-admin';

import { PluginResource } from '@src/types';
import { PluginType } from '@troverecommerce/ps-configs-api-client';

/**
 * Application data models and enums
 * Contains application-specific enums and constants
 */

/**
 *
 * Defines the available resource types that can be displayed and managed as React Admin Resources
 * within the partner dashboard interface.
 *
 */
export const enum DashboardResources {
  /** Catalog items resource type for managing product catalog data */
  CatalogItems = 'catalog-items',
  /** Variant SKU resource type for managing individual product variants */
  VariantSkus = 'variant-skus',
  /** Resource type used to manage recycle plugin configurations. */
  RecyclePlugins = 'plugins/recycle',
  /** Resource type used to manage repair plugin configurations. */
  RepairPlugins = 'plugins/repair',
  /** Resource type used to manage resale plugin configurations. */
  ResalePlugins = 'plugins/resale',
  /** Resource type used to manage trade-in plugin configurations. */
  TradeinPlugins = 'plugins/tradein',
  /** Repairs resource type for managing repair orders */
  Repairs = 'repairs',
  /** Catalog fields mapping upload resource for uploading display type mappings */
  CatalogFieldsMappingUpload = 'catalog-fields-mapping-upload',
  /** Pricing rules resource (resale pricing) */
  PricingRules = 'resalePricing/pricingRules',
  /** Condition discounts resource (resale pricing) */
  ConditionDiscounts = 'resalePricing/conditionDiscounts',
  /** Item adjusted prices resource (resale pricing, read-only) */
  ItemAdjustedPrices = 'resalePricing/itemAdjustedPrices',
  /** Shopify product redirects resource (read-only visualization) */
  ShopifyRedirects = 'shopify-redirects',
}

/**
 * Standard rejection reasons for repair requests and individual repair issues.
 * Used in both repair-level and issue-level rejection workflows.
 */
export const REJECTION_REASONS = [
  'Non-Repairable Damage Type',
  'Third-Party or Unauthorized Alterations',
  'Non-Authentic Product',
  'Item Outside Repair Program Scope',
  'Missing or Insufficient Information',
  'Unable to Determine Damage from Images',
] as const;

/**
 * Available catalog item availability options.
 * These values indicate where and how a catalog item can be traded in or purchased.
 */
export const CATALOG_AVAILABILITY_OPTIONS = [
  'Storefront',
  'Check-In',
  'Digital Trade-In',
  'In-Store Trade-In',
  'Repair',
] as const;

/**
 * Maps dashboard resources to their respective client plugin types.
 */
export const pluginResourceTypeMap: Record<PluginResource, PluginType> = {
  [DashboardResources.RecyclePlugins]: 'recycle',
  [DashboardResources.RepairPlugins]: 'repair',
  [DashboardResources.ResalePlugins]: 'resale',
  [DashboardResources.TradeinPlugins]: 'tradein',
};

export const pluginResourceProps: ResourceProps[] = [
  {
    name: DashboardResources.RecyclePlugins,
    options: {
      label: 'Recycle Plugin'
    },
  },
  {
    name: DashboardResources.RepairPlugins,
    options: {
      label: 'Repair Plugin'
    },
  },
  {
    name: DashboardResources.ResalePlugins,
    options: {
      label: 'Resale Plugin'
    },
  },
  {
    name: DashboardResources.TradeinPlugins,
    options: {
      label: 'Trade-in Plugin'
    },
  },
];
